// API 유틸리티 함수들
const API_BASE_URL = 'https://api.kkokk.io/api';
const API_KEY = 'kko-a1z2-xv39-82yh0-dqlo';

// 전역 캐시 시스템
const apiCache = new Map();
const defaultCacheTimeout = 120000; // 2분 (30초 → 2분으로 연장)

// 진행 중인 요청 추적
const pendingRequests = new Map();

export const getApiUrl = () => API_BASE_URL;

export const getApiKey = () => API_KEY;

// 캐시된 데이터 확인
export const getCachedData = (key, timeout = defaultCacheTimeout) => {
  const cached = apiCache.get(key);
  if (cached && Date.now() - cached.timestamp < timeout) {
    return cached.data;
  }
  return null;
};

// 데이터 캐시에 저장
export const setCachedData = (key, data, timeout = defaultCacheTimeout) => {
  apiCache.set(key, {
    data,
    timestamp: Date.now(),
    timeout
  });
};

// 캐시 무효화
export const invalidateCache = (key = null) => {
  if (key) {
    apiCache.delete(key);
  } else {
    apiCache.clear();
  }
};

// 중복 요청 방지를 위한 키 생성
const createRequestKey = (url, options) => {
  const method = options?.method || 'GET';
  const body = options?.body ? JSON.stringify(options.body) : '';
  return `${method}:${url}:${body}`;
};

// 최적화된 fetch 함수
export const optimizedFetch = async (url, options = {}, cacheKey = null, cacheTimeout = defaultCacheTimeout) => {
  const requestKey = createRequestKey(url, options);

  // 진행 중인 동일한 요청이 있는지 확인
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  // 캐시된 데이터가 있는지 확인
  if (cacheKey) {
    const cachedData = getCachedData(cacheKey, cacheTimeout);
    if (cachedData) {
      return cachedData;
    }
  }

  // 새로운 요청 생성
  const fetchPromise = fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'api_key': API_KEY,
      ...options.headers
    }
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();

    // 성공한 요청을 캐시에 저장
    if (cacheKey) {
      setCachedData(cacheKey, data, cacheTimeout);
    }

    return data;
  }).finally(() => {
    // 요청 완료 후 pendingRequests에서 제거
    pendingRequests.delete(requestKey);
  });

  // 진행 중인 요청으로 등록
  pendingRequests.set(requestKey, fetchPromise);

  return fetchPromise;
};

// 사용자 정보 조회 최적화 함수
export const fetchUserInfo = async (userId, token) => {
  const cacheKey = `userInfo_${userId}`;
  return optimizedFetch(
    `${API_BASE_URL}/user/userinfo`,
    {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, user_token: token })
    },
    cacheKey,
    30000 // 30초 캐시
  );
};

// 친구 상태 조회 최적화 함수
export const fetchFriendStatus = async (userId) => {
  const cacheKey = `friendStatus_${userId}`;
  return optimizedFetch(
    `${API_BASE_URL}/kkoworld/friend/status`,
    {
      method: 'POST',
      body: JSON.stringify({ requester_user_id: userId })
    },
    cacheKey,
    30000 // 30초 캐시
  );
};

// 랭킹 데이터 조회 최적화 함수
export const fetchRankingData = async (userId) => {
  const [topUsers, myRank] = await Promise.all([
    optimizedFetch(
      `${API_BASE_URL}/v2/studytimer/top3`,
      {},
      'topUsers',
      30000 // 30초 캐시
    ),
    optimizedFetch(
      `${API_BASE_URL}/v2/studytimer/userRank?user_id=${userId}`,
      {},
      `myRank_${userId}`,
      30000 // 30초 캐시
    )
  ]);

  return { topUsers, myRank };
};

// 통계 데이터 조회 최적화 함수
export const fetchStatisticsData = async (userId) => {
  const [studyData, sleepData] = await Promise.all([
    optimizedFetch(
      `${API_BASE_URL}/v2/studytimer/totalStudyTime7?user_id=${userId}`,
      {},
      `studyData_${userId}`,
      30000 // 30초 캐시
    ),
    optimizedFetch(
      `${API_BASE_URL}/v2/studytimer/sleepPattern7Days?user_id=${userId}`,
      {},
      `sleepData_${userId}`,
      30000 // 30초 캐시
    )
  ]);

  return { studyData, sleepData };
};

// StudyTimer API 일원화 래퍼
export const studyTimer = {
  async getRecords(userId) {
    return optimizedFetch(
      `${API_BASE_URL}/v2/studytimer/records?user_id=${userId}`,
      {},
      `studytimer_records_${userId}`,
      10000
    );
  },
  async toggle(userId, action, studyName = 'kkokkio_studycafe') {
    return optimizedFetch(
      `${API_BASE_URL}/v2/studytimer`,
      {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, action, study_name: studyName })
      }
    );
  }
};

// 캐시 상태 확인 (디버깅용)
export const getCacheStatus = () => {
  const status = {
    totalEntries: apiCache.size,
    pendingRequests: pendingRequests.size,
    entries: []
  };

  for (const [key, value] of apiCache.entries()) {
    status.entries.push({
      key,
      age: Date.now() - value.timestamp,
      timeout: value.timeout,
      isValid: Date.now() - value.timestamp < value.timeout
    });
  }

  return status;
}; 